"use client";

import { PreviewCard } from "@base-ui/react/preview-card";
import { useState } from "react";

import {
  SidebarItem,
  SidebarItemContent,
} from "@/components/layout/sidebar/sidebar-layout";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { Icon } from "@/components/ui/icon";
import { NotificationsPanelContent } from "@/features/notifications/components/list";
import { NotificationPanelLockProvider } from "@/features/notifications/lib/panel-lock";
import useIsMobile from "@/hooks/use-is-mobile";
import { cn } from "@/lib/utils";
import { useUnreadCount } from "@/features/notifications/hooks/use-unread-count";

/** Icon-only bell with a count bubble, for the quick-find row. */
function CompactBell({
  count,
  active,
  className,
}: {
  count: number;
  active?: boolean;
  className?: string;
}) {
  return (
    <span
      className={cn(
        // Same chrome as the quick-find box so the pair reads as one control set.
        "relative flex size-8 shrink-0 items-center justify-center rounded-md",
        "bg-contrast/[0.03] shadow-[inset_0_0_0_1px_var(--border-alpha)]",
        "text-contrast/50 transition-[background-color,box-shadow,color] duration-150",
        "hover:bg-contrast/[0.06] hover:text-heading",
        active && "bg-contrast/[0.06] text-heading",
        className,
      )}
    >
      <Icon name="IconBell2" size={16} />
      {count > 0 && (
        <span
          aria-hidden
          className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#ff3a64] px-1 text-[10px] font-semibold leading-none text-white shadow-[0_0_0_2px_var(--surface-frame)]"
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </span>
  );
}

export function NotificationsSidebarItem({
  expanded,
  compact = false,
}: {
  expanded: boolean;
  /** Render as an icon-only bell (used beside the quick-find box). */
  compact?: boolean;
}) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [menuLocked, setMenuLocked] = useState(false);
  const { data } = useUnreadCount();
  const count = data?.count ?? 0;
  const badge = count > 0 ? count : undefined;
  const ariaLabel =
    count > 0 ? `Notifications, ${count} unread` : "Notifications";

  function handleOpenChange(next: boolean) {
    if (!next && menuLocked) return;
    setOpen(next);
  }

  if (isMobile) {
    return (
      <>
        {compact ? (
          <button
            type="button"
            aria-label={ariaLabel}
            onClick={() => setOpen((v) => !v)}
            className="cursor-pointer rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ai/40"
          >
            <CompactBell count={count} active={open} />
          </button>
        ) : (
          <SidebarItem
            icon="IconBell2"
            label="Notifications"
            badge={badge}
            expanded={expanded}
            onClick={() => setOpen((v) => !v)}
            active={open}
          />
        )}
        <Drawer open={open} onOpenChange={handleOpenChange} direction="bottom">
          <DrawerContent className="flex max-h-[80svh] flex-col bg-surface-primary dark:bg-surface-secondary">
            <NotificationPanelLockProvider value={setMenuLocked}>
              <NotificationsPanelContent />
            </NotificationPanelLockProvider>
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  return (
    <PreviewCard.Root open={open} onOpenChange={handleOpenChange}>
      <PreviewCard.Trigger
        delay={120}
        closeDelay={300}
        render={(props) => (
          <button
            type="button"
            aria-label={ariaLabel}
            aria-haspopup="dialog"
            className={cn(
              "cursor-pointer rounded-md outline-none",
              "focus-visible:ring-2 focus-visible:ring-ai/40",
              !compact && "w-full",
            )}
            {...props}
          >
            {compact ? (
              <CompactBell count={count} active={open} />
            ) : (
              <SidebarItemContent
                icon="IconBell2"
                label="Notifications"
                badge={badge}
                expanded={expanded}
                active={open}
              />
            )}
          </button>
        )}
      />

      <PreviewCard.Portal>
        <PreviewCard.Positioner
          side="right"
          align="start"
          sideOffset={8}
          className="z-50"
        >
          <PreviewCard.Popup
            className={cn(
              "flex flex-col overflow-hidden",
              "w-87 h-118",
              "rounded-lg border border-border bg-surface-primary shadow-[var(--modal-shadow)] dark:bg-surface-secondary",
              "outline-none",
              "origin-[var(--transform-origin)]",
              "will-change-[scale,translate,opacity]",
              "transition-[scale,translate,opacity] duration-200",
              "ease-[cubic-bezier(0.16,1,0.3,1)]",
              "data-[starting-style]:scale-[0.96]",
              "data-[starting-style]:-translate-x-1",
              "data-[starting-style]:opacity-0",
              "data-[ending-style]:scale-[0.98]",
              "data-[ending-style]:opacity-0",
            )}
          >
            <NotificationPanelLockProvider value={setMenuLocked}>
              <NotificationsPanelContent />
            </NotificationPanelLockProvider>
          </PreviewCard.Popup>
        </PreviewCard.Positioner>
      </PreviewCard.Portal>
    </PreviewCard.Root>
  );
}
